import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
} from '@/utils/validx-util'
import type { BankAccountDetails } from './BankAccount'

/**
 * Account types the editor offers.
 */
export type BankAccountTypeOption = 'Checking' | 'Savings'

/**
 * View model for editing bank account details.
 */
export class BankAccountEditorViewModel {
  @observable private accessor _accountType: BankAccountTypeOption | '' = ''
  @observable private accessor _bankName = ''
  @observable private accessor _routingNumber = ''
  @observable private accessor _accountNumber = ''

  readonly validation = validationContext(this, {
    accountType: [required('Account type is required')],
    bankName: [
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Bank name must not exceed 100 characters'),
    ],
    routingNumber: [required('Routing number is required')],
    accountNumber: [required('Account number is required')],
  })

  @computed get accountType() {
    return this._accountType
  }

  set accountType(value: BankAccountTypeOption | '') {
    this._accountType = value
    this.validation.clearErrors('accountType')
  }

  @computed get bankName() {
    return this._bankName
  }

  set bankName(value: string) {
    this._bankName = value
    this.validation.clearErrors('bankName')
  }

  @computed get routingNumber() {
    return this._routingNumber
  }

  set routingNumber(value: string) {
    this._routingNumber = value
    this.validation.clearErrors('routingNumber')
  }

  @computed get accountNumber() {
    return this._accountNumber
  }

  set accountNumber(value: string) {
    this._accountNumber = value
    this.validation.clearErrors('accountNumber')
  }

  /**
   * Resets the state of the view model to that of the given bank account
   * details, or defaults if none is specified.
   */
  @action.bound
  reset(details?: BankAccountDetails) {
    this.accountType =
      !details || details.accountType === 'Unspecified'
        ? ''
        : details.accountType
    this.bankName = details?.bankName ?? ''
    this.routingNumber = details?.routingNumber ?? ''
    this.accountNumber = details?.accountNumber ?? ''
    this.validation.reset()
  }

  /**
   * Sets the account type, or clears it.
   */
  @action.bound
  setAccountType(accountType: BankAccountTypeOption | '') {
    this.accountType = accountType
  }

  /**
   * Maps the state of the VM to {@link BankAccountDetails}.
   */
  toBankAccountDetails(): BankAccountDetails {
    return {
      accountType: this.accountType || 'Unspecified',
      bankName: this.bankName,
      routingNumber: this.routingNumber,
      accountNumber: this.accountNumber,
    }
  }
}
